import * as types from './mutation-types';
import { translator } from './translator';
import { AppContext } from './context';
const Error = AppContext.Error;
import { Error as ApiError } from '../api/index';

export const actions = {
    /**
     * パラメーターのerrorDataがシステムエラーなのか判定し、該当すればエラーコードおよびエラーメッセージを設定する
     * システムエラーのパターン：
     * 1. ステータスコードが500以上且つ予期せぬエラーパターンに該当しない
     * @param context Vuex変数
     * @param errorData APIレスポンスボディ
     * @returns {Promise<Promise<never> | Promise<ApiError>>}
     */
    async checkSystemError(context, errorData: ApiError) {
        if (errorData.status >= 500) {
            context.commit(types.SYSTEM_ERROR, errorData.errors[0]);
            return Promise.reject();
        }
    },

    /**
     * パラメーターのerrorDataが予期せぬエラーなのか判定し、該当すればエラーコードおよびエラーメッセージを設定する
     * 予期せぬエラーのパターン：
     * 1. ステータスコードが400〜599の範囲外
     * 2. レスポンスボディにerrors<Array>が含まれていない
     * 3. 送信に失敗しレスポンスが空
     * @param context Vuex変数
     * @param errorData APIレスポンスボディ
     * @returns {Promise<Promise<never> | Promise<any>>} 予期せぬエラーの場合はPromise<reject>
     */
    async checkUnexpectedError(context, errorData: ApiError) {
        const status = errorData?.status;
        const errorContents: any = {};
        const i18n = context.state.i18n;

        // 送信に失敗した場合 (アドレス解決失敗、接続できない、その他)
        if (!errorData) {
            errorContents.errorCode = Error.Unexpected5XX.CODE;
            errorContents.errorMsg = translator(i18n, `error_message.${Error.Unexpected5XX.MESSAGE_KEY}`);

            // 予期しないエラーコードの場合（4XX系〜5XX系以外の場合）
        } else if (status < 400 || status >= 600 || !status) {
            errorContents.errorCode = Error.Unexpected.CODE;
            errorContents.errorMsg = translator(i18n, `error_message.${Error.Unexpected.MESSAGE_KEY}`);

            // 予期しない形式のレスポンスの場合（errorsがない場合）
        } else if (!(errorData.errors instanceof Array)) {
            // nginxが返すタイムアウトエラーの場合
            if (status == 504) {
                errorContents.errorCode = Error.GateWayTimeout.CODE;
                errorContents.errorMsg = translator(i18n, `error_message.${Error.GateWayTimeout.MESSAGE_KEY}`);

                // 5XX系エラーの場合
            } else if (status >= 500) {
                errorContents.errorCode = Error.Unexpected5XX.CODE;
                errorContents.errorMsg = translator(i18n, `error_message.${Error.Unexpected5XX.MESSAGE_KEY}`);

                // 4XX系エラーの場合
            } else {
                errorContents.errorCode = Error.Unexpected4XX.CODE;
                errorContents.errorMsg = translator(i18n, `error_message.${Error.Unexpected4XX.MESSAGE_KEY}`);
            }
        }

        // エラーがある場合
        if (errorContents.errorCode && errorContents.errorMsg) {
            context.commit(types.UNEXPECTED_ERROR, errorContents);
            return Promise.reject();
        }
    },

    /**
     * トーストメッセージを出力する
     * @param {any} commit Vuex変数
     * @param {string} message トーストメッセージ
     * @returns {Promise<void>}
     */
    async toastMessage({ commit }, message: string) {
        commit(types.TOAST_MESSAGE, { message });
    },

    /**
     * トーストメッセージを消す
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async dealMessage({ commit }) {
        commit(types.DEAL_MESSAGE);
    },

    /**
     * システムエラーまたは予期せぬエラーを消す
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async dealError({ commit }) {
        commit(types.DEAL_ERROR);
    },

    /**
     * 処理中フラグおよびメッセージを設定する
     * @param {any} commit Vuex変数
     * @param {{running: boolean; msgKey: string}} payload 処理中情報
     * @returns {Promise<void>}
     */
    async process({ commit }, payload: { running: boolean; msgKey: string }) {
        commit(types.PROCESS, {
            running: payload.running,
            msgKey: payload.msgKey,
        });
    },

    /**
     * 表示言語設定を変更する
     * @param {any} commit Vuex変数
     * @param {{language: string}} payload 表示言語情報
     * @returns {Promise<void>}
     */
    async changeDisplayLanguage({ commit }, payload: { language: string | null }) {
        commit(types.LANGUAGE, { language: payload.language });
    },
};
